<script lang="ts">
  import { onMount } from "svelte"
  import { xhr, toast } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import Button from "$app/components/ButtonSpinner.svelte"
  import { ChevronRight } from "lucide-svelte"
  import Goal from "$app/Pages/Plans/Onboarding/components/Goal.svelte"
  import { appStore } from "$app/store.svelte"

  let long_term_goal = $state({} as GoalType)
  let annual_goal = $state({} as GoalType)
  let quarterly_goal = $state({} as GoalType)

  let completed = $state(true)
  let saving = $state(false)
  let errors: Record<string, any> = $state({})

  const initialForm = {
    id: null,
    name: "",
    description: "",
    obstacles_text: "",
    action_plan: "",
    assignee_type: "Department",
    assignee_id: 0,
    status: "on_track",
    goal_id: null,
    visibility: "plan"
  }

  let form = $state({ ...initialForm })
  let loaded = $state(false)

  async function getGoalsAndTask(): Promise<void> {
    try {
      const resp = await xhr.get(
        `/plan_onboarding/${appStore.plan.id}/get_goals.json?goal_type=task`
      )

      long_term_goal = resp.data.long_term_goal
      annual_goal = resp.data.annual_goal
      quarterly_goal = resp.data.quarterly_goal

      form.id = resp.data.task.id
      form.name = resp.data.task.name
      if (!resp.data.task.assignee_id) {
        form.assignee_id = quarterly_goal.assignee_id
      }
      form.goal_id = quarterly_goal.id
      form.description = resp.data.task.description
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading your goals", "error")
    } finally {
      loaded = true
    }
  }

  async function nextStep(): Promise<void> {
    saving = true
    errors = {}

    try {
      const resp = await xhr.post<{
        plan: PlanType
        onboarding: OnboardingType
      }>(`/plan_onboarding/${appStore.plan.id}/create_task.json`, {
        task: form
      })

      appStore.plan.onboarding_steps = resp.data.plan.onboarding_steps
      appStore.onboarding = resp.data.onboarding
    } catch (e: any) {
      console.error(e)
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      saving = false
    }
  }

  onMount(() => {
    getGoalsAndTask()
  })
</script>

<h2 class="text-2xl text-center text-white">Guided Initial Goals Set Up</h2>
<h3 class="mt-2 text-xl font-bold text-center text-blue-400">
  <span class="text-3xl text-bold">4. </span>Now Add a Task
</h3>
<div class="max-w-3xl mx-auto">
  <p class="mt-6 text-lg text-white">
    Create tasks to help keep your team members accountable to reaching the
    quarterly goals that are set. Tasks can be assigned to a department or a
    team member. Please note that during this initial setup you can only assign
    tasks to departments but once you onboard your team then you will be able to
    assign tasks to them.
  </p>

  {#if loaded}
    <div class="flex items-center pt-6">
      <p class="text-2xl text-white">This is the heriarchy for your task.</p>
    </div>

    <div class="scale-75">
      <ul class="hierarchy" style="transform: translateX(-100px)">
        <li>
          <Goal name={long_term_goal.name} goalType="long_term_goal" />

          <ul class="ml-8">
            <li>
              <Goal name={annual_goal.name} goalType="annual_goal" />
              <ul class="ml-8">
                <li>
                  <Goal name={quarterly_goal.name} goalType="quarterly_goal" />
                  <ul class="ml-8">
                    <li>
                      <Goal name={form.name} goalType="task" />
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
        </li>
      </ul>
    </div>

    {#if form.goal_id !== null}
      <div class="ml-6">
        <p class="mt-6 text-2xl text-white">Enter the Task details:</p>
        <div class="space-y-4 mt-2 max-w-xl">
          <div>
            <div class="mt-6 grid grid-cols-2 gap-y-3 gap-x-4">
              <div class="col-span-2">
                <label for="name" class="text-white text-lg">Name </label>
                <div class="mt-2 input-cont" class:error={errors.name}>
                  <input
                    type="text"
                    placeholder="Task name"
                    bind:value={form.name}
                    class="input input-lg"
                  />
                  <div class="msg">{errors.name}</div>
                </div>
              </div>
              <div class="col-span-2" class:error={errors.assignee_id}>
                <label for="assignee_id" class="text-white text-lg"
                  >Assignee</label
                >
                <div class="mt-1 input-cont" class:error={errors.assignee_id}>
                  <select
                    class="input input-lg"
                    id="goal_assignee_id"
                    bind:value={form.assignee_id}
                  >
                    {#if appStore.departments.length}
                      {#each appStore.departments as dept (dept.id)}
                        <option value={dept.id}>{dept.name}</option>
                      {/each}
                    {:else}
                      <option value="">No Departments</option>
                    {/if}
                  </select>

                  <div class="msg">{errors.assignee_id}</div>
                </div>
              </div>
              <div class="mt-6 flex justify-start gap-x-5">
                <Button
                  onclick={nextStep}
                  disabled={!completed}
                  processing={saving}
                  class="btn btn-rounded success btn-big flex items-center gap-1"
                >
                  <span class="font-light text-core"> CREATE TASK </span>
                  <ChevronRight class="size-6 text-core" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    {/if}
  {/if}
</div>
